import { useRef, useState } from 'react';
import type { PointerEvent } from 'react';
import { message } from 'antd';
import { useIntl } from 'react-intl';
import type { WorkOrderPhotoUploader } from '@src/services/WorkOrderPhotoService';

export interface SignatureInputValue {
  url: string;
  uploading?: boolean;
  clarificationValue?: string;
}
export interface SignatureInputProps {
  value?: SignatureInputValue;
  onChange?: (value?: SignatureInputValue) => void;
  includeClarification?: boolean;
  organizationId: string;
  divisionId: string;
  photoService: WorkOrderPhotoUploader;
}

export default function useSignatureInput(props: SignatureInputProps) {
  const intl = useIntl();
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [open, setOpen] = useState(false);
  const [hasInk, setHasInk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState('');
  const clear = () => {
    if (canvas.current) {
      canvas.current.getContext('2d')?.clearRect(0, 0, canvas.current.width, canvas.current.height);
    }
    setHasInk(false);
  };
  const draw = (event: PointerEvent<HTMLCanvasElement>, start = false) => {
    if (saving) return;
    const element = event.currentTarget;
    const context = element.getContext('2d');
    if (!context) return;
    const rect = element.getBoundingClientRect();
    const x = ((event.clientX - rect.left) * element.width) / rect.width;
    const y = ((event.clientY - rect.top) * element.height) / rect.height;
    if (start) {
      drawing.current = true;
      element.setPointerCapture(event.pointerId);
      context.beginPath();
      context.moveTo(x, y);
    }
    if (!drawing.current) return;
    context.lineWidth = 2;
    context.lineCap = 'round';
    context.lineTo(x, y);
    context.stroke();
    setHasInk(true);
  };
  const sign = async () => {
    if (!canvas.current || !hasInk || saving) return;
    setSaving(true);
    props.onChange?.({
      url: '',
      uploading: true,
      clarificationValue: props.value?.clarificationValue,
    });
    try {
      const image = canvas.current.toDataURL('image/png');
      const blob = await new Promise<Blob>((resolve, reject) => {
        canvas.current?.toBlob(
          result => (result ? resolve(result) : reject(new Error('PNG'))),
          'image/png',
        );
      });
      const result = await props.photoService.upload({
        organizationId: props.organizationId,
        divisionId: props.divisionId,
        file: new File([blob], 'signature.png', { type: 'image/png' }),
        kind: 'signature',
      });
      props.onChange?.({ url: result.url, clarificationValue: props.value?.clarificationValue });
      setPreview(image);
      setOpen(false);
    } catch {
      props.onChange?.(props.value);
      message.error(intl.formatMessage({ id: 'dynamic.form.photo.upload.error' }));
    } finally {
      setSaving(false);
    }
  };
  return {
    canvas,
    clear,
    draw,
    hasInk,
    open,
    preview,
    saving,
    setOpen,
    sign,
    stop: () => {
      drawing.current = false;
    },
  };
}
